import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getAllTimelines,
  saveTimeline,
  softDeleteTimeline,
  duplicateTimeline,
  updateTimeline,
} from "../../services/azureTimelinesService";
import { getAllProjectsWithTags } from "../../services/azureProjectsService";
import { canEdit, canCreateTimeline } from "../../utils/permissions";
import { useAuth } from "../../hooks/useAuth";
import { TimelineItemsProvider } from "../../services/timeline/timelineItemsProvider";

const getInitials = (name) => {
  if (!name) return "?";
  const displayName = name.includes("@") ? name.split("@")[0] : name;
  const parts = displayName.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return displayName.substring(0, 2).toUpperCase();
};

const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const TimelineList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [timelines, setTimelines] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("updatedAt");
  const [sortDirection, setSortDirection] = useState("desc");

  // Modal "Neuer Terminplan"
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: "", linkedProjectCode: "" });
  const [formError, setFormError] = useState("");

  // Rechtsklick-Kontextmenü + Inline-Umbenennen (wie EstimateList)
  const [contextMenu, setContextMenu] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef(null);

  const projectFilter = searchParams.get("project");
  // GL/PL: verwalten (umbenennen/duplizieren/löschen). Internes Team: eigene Pläne eröffnen.
  const canManage = canEdit(user, "termine");
  const canCreate = canCreateTimeline(user);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [timelinesData, projectsData] = await Promise.all([
        getAllTimelines(),
        getAllProjectsWithTags("project"),
      ]);
      setTimelines(timelinesData);
      setProjects(projectsData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredTimelines = timelines
    .filter((tl) => {
      if (projectFilter && tl.linkedProjectCode !== projectFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          tl.name.toLowerCase().includes(term) ||
          tl.linkedProjectCode.toLowerCase().includes(term)
        );
      }
      return true;
    })
    .sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      const valA = (a[sortField] || "").toLowerCase();
      const valB = (b[sortField] || "").toLowerCase();
      return valA.localeCompare(valB) * dir;
    });

  const handleSave = async () => {
    if (!formData.linkedProjectCode) {
      setFormError("Projekt ist erforderlich");
      return;
    }
    try {
      setSaving(true);
      setFormError("");
      const proj = projects.find((p) => p.code === formData.linkedProjectCode);
      const name = formData.name.trim() || (proj ? `Terminplan ${proj.code}` : "Terminplan");
      // Wer kein:e GL/PL ist, wird beim Eröffnen automatisch als Bearbeiter:in eingetragen
      // (GL/PL dürfen ohnehin jeden Plan bearbeiten – siehe canEditTimeline).
      const tm = user?.teamMember;
      const allowedEditors =
        !canManage && tm
          ? [{ uuid: tm.employeeUuid || "", teamId: tm.teamId || "", name: tm.name || "" }]
          : [];
      const saved = await saveTimeline({
        name,
        linkedProjectCode: formData.linkedProjectCode,
        createdBy: user?.userDetails || user?.email || user?.name || "",
        allowedEditors,
      });
      setShowModal(false);
      setFormData({ name: "", linkedProjectCode: "" });
      navigate(`/termine/${saved.id}`);
    } catch (error) {
      setFormError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, timeline) => {
    e.stopPropagation();
    if (!window.confirm(`Terminplan "${timeline.name}" wirklich löschen? Er wird 30 Tage aufbewahrt.`)) return;
    try {
      await softDeleteTimeline(timeline.id);
      setTimelines((prev) => prev.filter((tl) => tl.id !== timeline.id));
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handleDuplicate = async (e, timeline) => {
    e.stopPropagation();
    let saved = null;
    try {
      const createdBy = user?.userDetails || user?.email || user?.name || "";
      saved = await duplicateTimeline(timeline, createdBy);
      // Inhalt (Tasks/Gruppen/Zuständige/Settings) mitkopieren.
      const provider = new TimelineItemsProvider();
      await provider.copyAllItems(timeline.id, saved.id);
      navigate(`/termine/${saved.id}`);
    } catch (err) {
      console.error("Duplicate error:", err);
      // Cleanup: halbkopiertes Ziel wieder entfernen.
      if (saved?.id) {
        try {
          await softDeleteTimeline(saved.id);
        } catch (cleanupErr) {
          console.error("Cleanup nach Duplicate-Fehler fehlgeschlagen:", cleanupErr);
        }
      }
      window.alert(
        `Duplizieren fehlgeschlagen: ${err?.message || "Unbekannter Fehler"}\n\n` +
          `Das Ziel wurde wieder entfernt. Bitte erneut versuchen.`
      );
    }
  };

  const handleRowClick = (timeline) => {
    if (editingId === timeline.id) return;
    navigate(`/termine/${timeline.id}`);
  };

  const handleContextMenu = (e, timeline) => {
    if (!canManage) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, timeline });
  };

  const startRename = (timeline) => {
    setContextMenu(null);
    setEditingId(timeline.id);
    setEditingName(timeline.name || "");
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveRename = async () => {
    if (!editingId) return;
    const trimmed = editingName.trim();
    const current = timelines.find((t) => t.id === editingId);
    if (!current || !trimmed || trimmed === current.name) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    try {
      await updateTimeline(editingId, { ...current, name: trimmed });
      setTimelines((prev) =>
        prev.map((tl) => (tl.id === editingId ? { ...tl, name: trimmed } : tl))
      );
      cancelRename();
    } catch (err) {
      console.error("Rename error:", err);
    } finally {
      setRenameSaving(false);
    }
  };

  // Kontextmenü schliessen bei Klick/Scroll/Escape.
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const handleKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [contextMenu]);

  useEffect(() => {
    if (editingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [editingId]);

  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    return <span className="sort-indicator">{sortDirection === "asc" ? "↑" : "↓"}</span>;
  };

  const getProjectName = (code) => {
    if (!code) return "";
    const project = projects.find((p) => p.code === code);
    return project ? `${code} - ${project.title}` : code;
  };

  if (loading) {
    return <div className="loading">Terminpläne werden geladen...</div>;
  }

  return (
    <div className="estimate-list">
      <div className="team-controls">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Terminplan suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-controls">
          {projectFilter && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate("/termine")}>
              Filter aufheben
            </button>
          )}
        </div>
        <div className="table-info">
          {filteredTimelines.length} {filteredTimelines.length === 1 ? "Terminplan" : "Terminpläne"}
          {projectFilter && ` (Projekt: ${projectFilter})`}
        </div>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            Neuer Terminplan
          </button>
        )}
      </div>

      <div className="team-table-container">
        <table className="team-table team-table-compact">
          <thead>
            <tr>
              <th>Projekt</th>
              <th
                className={`sortable ${sortField === "name" ? "active" : ""}`}
                onClick={() => handleSort("name")}
              >
                Name {renderSortIndicator("name")}
              </th>
              <th
                className={`sortable ${sortField === "updatedAt" ? "active" : ""}`}
                onClick={() => handleSort("updatedAt")}
              >
                Zuletzt bearbeitet {renderSortIndicator("updatedAt")}
              </th>
              {canManage && <th></th>}
            </tr>
          </thead>
          <tbody>
            {filteredTimelines.length === 0 ? (
              <tr>
                <td
                  colSpan={canManage ? 4 : 3}
                  style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}
                >
                  {projectFilter
                    ? "Keine Terminpläne für dieses Projekt vorhanden."
                    : "Keine Terminpläne vorhanden."}
                </td>
              </tr>
            ) : (
              filteredTimelines.map((tl) => {
                const now = Date.now();
                const recentEditors = (tl.editors || [])
                  .filter((e) => now - new Date(e.lastActive).getTime() < THIRTY_DAYS)
                  .sort((a, b) => new Date(b.lastActive) - new Date(a.lastActive));
                const isEditing = editingId === tl.id;
                return (
                  <tr
                    key={tl.id}
                    className="team-row clickable-row"
                    onClick={() => handleRowClick(tl)}
                    onContextMenu={(e) => handleContextMenu(e, tl)}
                  >
                    <td title={getProjectName(tl.linkedProjectCode)}>{tl.linkedProjectCode || ""}</td>
                    <td className="cell-name" onClick={(e) => { if (isEditing) e.stopPropagation(); }}>
                      {isEditing ? (
                        <input
                          ref={renameInputRef}
                          type="text"
                          className="cell-name-input"
                          value={editingName}
                          disabled={renameSaving}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={saveRename}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              saveRename();
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              cancelRename();
                            }
                          }}
                        />
                      ) : (
                        tl.name
                      )}
                    </td>
                    <td className="cell-updated">
                      {tl.updatedAt ? new Date(tl.updatedAt).toLocaleDateString("de-CH") : ""}
                      {recentEditors.length > 0 && (
                        <span className="editor-avatars">
                          {recentEditors.slice(0, 3).map((editor) => (
                            <span
                              key={editor.name}
                              className="editor-avatar"
                              title={`${editor.name} (${new Date(editor.lastActive).toLocaleDateString("de-CH")})`}
                            >
                              {getInitials(editor.name)}
                            </span>
                          ))}
                          {recentEditors.length > 3 && (
                            <span
                              className="editor-avatar editor-avatar-more"
                              title={recentEditors.map((e) => e.name).join(", ")}
                            >
                              +{recentEditors.length - 3}
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    {canManage && (
                      <td className="cell-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="action-btn"
                          onClick={(e) => handleDuplicate(e, tl)}
                          title="Terminplan duplizieren"
                        >
                          &#x2398;
                        </button>
                        <button
                          className="action-btn action-btn-danger"
                          onClick={(e) => handleDelete(e, tl)}
                          title="Terminplan löschen"
                        >
                          &times;
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Rechtsklick-Kontextmenü */}
      {contextMenu && (
        <div
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="context-menu-header" title={contextMenu.timeline.name}>
            {contextMenu.timeline.name}
          </div>
          <button type="button" className="context-menu-item" onClick={() => startRename(contextMenu.timeline)}>
            Umbenennen
          </button>
        </div>
      )}

      {/* Modal: Neuer Terminplan */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content estimate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Neuer Terminplan</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {formError && <div className="error-message">{formError}</div>}
              <div className="form-group">
                <label>Projekt *</label>
                <select
                  value={formData.linkedProjectCode}
                  onChange={(e) => setFormData({ ...formData, linkedProjectCode: e.target.value })}
                  autoFocus
                >
                  <option value="">-- Projekt wählen --</option>
                  {projects.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.code} - {p.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Leer lassen für Standardname"
                  disabled={!formData.linkedProjectCode}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>
                Abbrechen
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Speichern..." : "Terminplan erstellen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineList;
